import { createContext, useContext, useEffect, useState, useRef } from "react";

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("wk_lang") || "es");
  useEffect(() => { localStorage.setItem("wk_lang", lang); document.documentElement.lang = lang; }, [lang]);
  const toggle = () => setLang((l) => (l === "es" ? "en" : "es"));
  return <LangContext.Provider value={{ lang, setLang, toggle, t: content[lang] }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);

// Reveal-on-scroll wrapper (discrete, professional)
export function Reveal({ children, className = "", delay = 0, as: Tag = "div", ...rest }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) { setVis(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} {...rest} style={{ animationDelay: `${delay}ms` }} className={`wk-reveal ${vis ? "is-visible" : ""} ${className}`}>{children}</Tag>;
}

export const content = {
  es: {
    nav: { inicio: "Inicio", nosotros: "Nosotros", soluciones: "Soluciones", industrias: "Industrias", servicios: "Servicios", ompec: "OM-PEC Safety", marcas: "Marcas", contacto: "Contacto" },
    cta: { quote: "Solicitar cotización", solutions: "Conoce nuestras soluciones", contact: "Contáctanos", portal: "Portal interno", ompec: "Conoce OM-PEC Safety" },
    hero: {
      eyebrow: "WEST KAC COMPANY · Industrial Solutions & Services",
      title: "Simplificamos la forma en que trabajas.",
      concept: "SUPPLY. SOLVE. PROTECT.",
      desc: "Desde suministro industrial y equipos hasta servicios técnicos, conectividad y soluciones de seguridad, ayudamos a las empresas a resolver las necesidades prácticas que mantienen sus operaciones en marcha.",
    },
    pillars: {
      title: "Un ecosistema, tres capacidades",
      subtitle: "SUPPLY. SOLVE. PROTECT.",
      items: [
        { key: "supply", tag: "SUPPLY", h: "Suministramos", d: "Productos, herramientas, equipos y consumibles que mantienen tu operación funcionando.", list: ["Herramientas", "Equipos", "Consumibles", "EPP", "Suministros industriales"], cta: "Explorar suministro" },
        { key: "solve", tag: "SOLVE", h: "Resolvemos", d: "Encontramos soluciones prácticas para las necesidades específicas de tu operación.", list: ["Instalaciones", "Mantenimiento", "Infraestructura", "Conectividad", "Soluciones técnicas"], cta: "Conocer servicios" },
        { key: "protect", tag: "PROTECT", h: "Protegemos", d: "Desarrollamos la seguridad y las competencias de las personas mediante OM-PEC Safety.", list: ["Seguridad industrial", "Capacitación", "E-learning", "STPS", "DC-3"], cta: "Conocer OM-PEC Safety" },
      ],
    },
    help: {
      title: "¿Cómo podemos ayudarte?",
      items: [
        { h: "Suministro industrial", d: "Herramientas, equipos, consumibles, EPP y suministros." },
        { h: "Servicios técnicos", d: "Instalación, mantenimiento, infraestructura y soluciones técnicas." },
        { h: "Seguridad y capacitación", d: "Capacitación presencial y online, seguridad industrial y soluciones especializadas." },
        { h: "Conectividad e infraestructura", d: "Soluciones de conectividad, infraestructura y necesidades operativas." },
      ],
    },
    history: {
      title: "Nacimos en el campo. Crecemos con la industria.",
      p: [
        "West KAC nació apoyando operaciones de mantenimiento en el sector de la energía eólica, proporcionando herramientas, equipos, consumibles y soluciones prácticas para las necesidades reales de los equipos de campo.",
        "Esa experiencia nos enseñó algo fundamental: cada operación tiene necesidades diferentes y muchas veces la solución no está en un catálogo.",
        "Por eso evolucionamos de un proveedor de productos a una empresa enfocada en suministrar, resolver y desarrollar soluciones para la industria.",
      ],
      steps: ["Proveedor", "Solucionador", "Integrador", "Socio industrial"],
    },
    industries: {
      title: "Soluciones para diferentes industrias",
      items: [
        { h: "Energía eólica", d: "Suministro, herramientas, consumibles, EPP y soluciones para el mantenimiento de aerogeneradores." },
        { h: "Manufactura", d: "Equipos, herramientas, consumibles y soluciones para mantenimiento y operación industrial." },
        { h: "Automotriz", d: "Soluciones para líneas de producción, mantenimiento, seguridad y operaciones de manufactura." },
        { h: "Energía", d: "Equipamiento y soluciones para operaciones que requieren confiabilidad, seguridad y respuesta técnica." },
        { h: "Construcción", d: "Herramientas, equipos y soluciones de seguridad para trabajos en campo." },
        { h: "Mantenimiento industrial", d: "Productos y soluciones para mantenimiento preventivo, correctivo y operaciones de campo." },
      ],
    },
    ompec: {
      title: "Seguridad que forma parte de la solución.",
      brand: "OM-PEC SAFETY",
      claim: "Protegemos personas, fortalecemos empresas.",
      sub: "A WEST KAC COMPANY",
      p: [
        "OM-PEC Safety es la plataforma especializada de WEST KAC para seguridad industrial y capacitación.",
        "Ofrecemos capacitación presencial y en línea, cursos con enfoque STPS, emisión de DC-3 y soluciones de seguridad diseñadas para las necesidades reales de las empresas.",
      ],
      tags: ["Capacitación presencial", "E-learning", "Cursos STPS", "DC-3", "Seguridad industrial", "Trabajo en alturas", "Protección contra caídas", "EPP"],
    },
    values: {
      title: "Más que un proveedor.",
      lead: "Nuestro objetivo no es solamente proporcionar productos. Buscamos comprender el problema, encontrar una solución y hacerla realidad.",
      items: [
        { h: "Experiencia industrial", d: "Conocemos las exigencias de las operaciones reales." },
        { h: "Soluciones prácticas", d: "Buscamos alternativas que funcionen en el mundo real." },
        { h: "Atención personalizada", d: "Cada necesidad puede requerir una solución diferente." },
        { h: "Un solo socio", d: "Suministro, servicios, seguridad y capacitación bajo un mismo ecosistema." },
      ],
    },
    brands: { title: "Marcas y socios", sub: "Trabajamos con fabricantes y marcas líderes de la industria.", pending: "Logo pendiente" },
    quote: {
      title: "Cuéntanos qué necesitas.",
      desc: "¿Buscas un producto específico, herramientas, equipos, EPP, capacitación o una solución para una necesidad particular? Envíanos tu requerimiento y nuestro equipo te ayudará a encontrar la solución adecuada.",
      note: "También puedes enviarnos tu lista de materiales, especificación técnica o documento de requerimientos.",
      f: { nombre: "Nombre", empresa: "Empresa", email: "Correo", telefono: "Teléfono", pais: "País", industria: "Industria", tipo: "Tipo de requerimiento", descripcion: "Descripción", privacidad: "Acepto el aviso de privacidad y el tratamiento de mis datos.", submit: "Solicitar cotización", tipos: ["Suministro / Productos", "Herramientas y equipos", "EPP", "Servicios técnicos", "Capacitación / Seguridad", "Otro"] },
      ok: "¡Gracias! Recibimos tu requerimiento. Nuestro equipo te contactará muy pronto.",
      err: "No se pudo enviar. Verifica los campos e inténtalo de nuevo.",
    },
    contact: {
      title: "Hablemos de tu próxima solución.",
      desc: "¿Tienes un proyecto, requerimiento o necesidad específica? Estamos listos para ayudarte.",
      country: "México",
      countryNote: "Presencia en Estados Unidos — próximamente.",
    },
    footer: {
      tagline: "Simplifying the way you work.",
      about: "Industrial Solutions & Services. Suministro, servicios técnicos y soluciones de seguridad para la industria.",
      colEmpresa: "Empresa", empresa: ["Nosotros", "Industrias", "Servicios", "Marcas"],
      colSol: "Soluciones", sol: ["Suministro", "Herramientas", "Equipos", "Seguridad", "Soluciones técnicas"],
      colOmpec: "OM-PEC Safety", ompec: ["Capacitación", "E-learning", "STPS", "DC-3", "Seguridad industrial"],
      colContacto: "Contacto", legal: "Aviso de privacidad y términos.", rights: "Todos los derechos reservados.",
    },
  },
  en: {
    nav: { inicio: "Home", nosotros: "About", soluciones: "Solutions", industrias: "Industries", servicios: "Services", ompec: "OM-PEC Safety", marcas: "Brands", contacto: "Contact" },
    cta: { quote: "Request a quote", solutions: "Explore our solutions", contact: "Contact us", portal: "Internal portal", ompec: "Discover OM-PEC Safety" },
    hero: {
      eyebrow: "WEST KAC COMPANY · Industrial Solutions & Services",
      title: "Simplifying the way you work.",
      concept: "SUPPLY. SOLVE. PROTECT.",
      desc: "From industrial supply and equipment to technical services, connectivity and safety solutions, we help companies solve the practical needs that keep their operations running.",
    },
    pillars: {
      title: "One ecosystem, three capabilities",
      subtitle: "SUPPLY. SOLVE. PROTECT.",
      items: [
        { key: "supply", tag: "SUPPLY", h: "We provide", d: "The products, tools and equipment your operation needs to keep running.", list: ["Tools", "Equipment", "Consumables", "PPE", "Industrial supplies"], cta: "Explore supply" },
        { key: "solve", tag: "SOLVE", h: "We solve", d: "We develop practical solutions for your operational challenges.", list: ["Installations", "Maintenance", "Infrastructure", "Connectivity", "Technical solutions"], cta: "See services" },
        { key: "protect", tag: "PROTECT", h: "We protect", d: "Through OM-PEC Safety, we help train and protect your people.", list: ["Industrial safety", "Training", "E-learning", "Compliance", "Certifications"], cta: "Discover OM-PEC Safety" },
      ],
    },
    help: {
      title: "How can we help you?",
      items: [
        { h: "Industrial supply", d: "Tools, equipment, consumables, PPE and supplies." },
        { h: "Technical services", d: "Installation, maintenance, infrastructure and technical solutions." },
        { h: "Safety & training", d: "On-site and online training, industrial safety and specialized solutions." },
        { h: "Connectivity & infrastructure", d: "Connectivity, infrastructure and operational solutions." },
      ],
    },
    history: {
      title: "Born in the field. Growing with the industry.",
      p: [
        "West KAC began supporting maintenance operations in the wind energy sector, providing tools, equipment, consumables and practical solutions for the real needs of field teams.",
        "That experience taught us something fundamental: every operation has different needs, and the solution often isn't in a catalog.",
        "That is why we evolved from a product supplier into a company focused on supplying, solving and developing solutions for industry.",
      ],
      steps: ["Supplier", "Problem-solver", "Integrator", "Industrial partner"],
    },
    industries: {
      title: "Solutions for different industries",
      items: [
        { h: "Wind energy", d: "Supply, tools, consumables, PPE and solutions for wind turbine maintenance operations." },
        { h: "Manufacturing", d: "Equipment, tools, consumables and solutions for industrial maintenance and operation." },
        { h: "Automotive", d: "Solutions for production lines, maintenance, safety and manufacturing operations." },
        { h: "Energy", d: "Equipment and solutions for operations that demand reliability, safety and technical response." },
        { h: "Construction", d: "Tools, equipment and safety solutions for field work." },
        { h: "Industrial maintenance", d: "Products and solutions for preventive, corrective and field maintenance operations." },
      ],
    },
    ompec: {
      title: "Safety that is part of the solution.",
      brand: "OM-PEC SAFETY",
      claim: "Protecting people, strengthening businesses.",
      sub: "A WEST KAC COMPANY",
      p: [
        "OM-PEC Safety is WEST KAC's specialized platform for industrial safety and training.",
        "We offer on-site and online training, courses aligned with Mexican STPS regulations (with official DC-3 certificates), and safety solutions designed for the real needs of companies. Capabilities for the U.S. market are being developed.",
      ],
      tags: ["On-site training", "E-learning", "STPS courses (MX)", "DC-3 (MX)", "Industrial safety", "Work at height", "Fall protection", "PPE"],
    },
    values: {
      title: "More than a supplier.",
      lead: "Our goal is not only to provide products. We seek to understand the problem, find a solution and make it real.",
      items: [
        { h: "Industrial experience", d: "We know the demands of real-world operations." },
        { h: "Practical solutions", d: "We look for alternatives that actually work in the field." },
        { h: "Personalized attention", d: "Every need may require a different solution." },
        { h: "A single partner", d: "Supply, services, safety and training under one ecosystem." },
      ],
    },
    brands: { title: "Brands & partners", sub: "We work with leading industry manufacturers and brands.", pending: "Logo pending" },
    quote: {
      title: "Tell us what you need.",
      desc: "Looking for a specific product, tools, equipment, PPE, training or a solution for a particular need? Send us your requirement and our team will help you find the right solution.",
      note: "You can also send us your bill of materials, technical specification or requirements document.",
      f: { nombre: "Name", empresa: "Company", email: "Email", telefono: "Phone", pais: "Country", industria: "Industry", tipo: "Requirement type", descripcion: "Description", privacidad: "I accept the privacy policy and the processing of my data.", submit: "Request a quote", tipos: ["Supply / Products", "Tools & equipment", "PPE", "Technical services", "Training / Safety", "Other"] },
      ok: "Thank you! We received your requirement. Our team will contact you shortly.",
      err: "Could not send. Please check the fields and try again.",
    },
    contact: {
      title: "Let's talk about your next solution.",
      desc: "Do you have a project, requirement or specific need? We're ready to help.",
      country: "Mexico",
      countryNote: "U.S. presence — coming soon.",
    },
    footer: {
      tagline: "Simplifying the way you work.",
      about: "Industrial Solutions & Services. Supply, technical services and safety solutions for industry.",
      colEmpresa: "Company", empresa: ["About", "Industries", "Services", "Brands"],
      colSol: "Solutions", sol: ["Supply", "Tools", "Equipment", "Safety", "Technical solutions"],
      colOmpec: "OM-PEC Safety", ompec: ["Training", "E-learning", "STPS", "DC-3", "Industrial safety"],
      colContacto: "Contact", legal: "Privacy policy and terms.", rights: "All rights reserved.",
    },
  },
};
